import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";

import Icon from "./icons";
import { DATE_FORMATS, DATE_OPTIONS, DEFAULT_DATE_FORMAT } from "./resourceTypes";
import { emptyDateConfig } from "./payload";

/**
 * The second row a Key/Type/Value row grows when its type is Date.
 *
 * One component for every table that has data types — Parameters, Headers, Form
 * Data and x-www-form-urlencoded — because ParamsTable is shared by all four.
 * It replaces the antd form's DateConfigSelect and writes the identical object:
 *
 *   { dateOption: "fixedDate" | "today" | "daysBefore",
 *     timeUnits:  number,          // days, for daysBefore
 *     fixedDate:  string | null,   // already formatted with dateFormat
 *     dateFormat: string }
 *
 * Controlled, unlike DateConfigSelect — the row owns the config and this only
 * renders it. The old one mirrored every field into local state and re-synced
 * from props, which is why changing a format there could leave the two copies
 * disagreeing.
 *
 * What each option means at run time (five request builders evaluate it, all
 * identically — editorSlice, boardSlice, the two widget lists and ResourceEditor):
 *
 *   daysBefore   N days back from now, start of day, UTC. Worked out per run.
 *   today        now, worked out per run.
 *   fixedDate    one pinned date, formatted once here and stored.
 */
/* The Days Before count. A plain text box that only takes digits, so it can be
   cleared and retyped like any field — the number input before clamped every
   change to ≥ 1, which put the 1 back the moment it was deleted.

   The box may sit empty while typing; the stored count only changes for a real
   number. Leaving it empty or at 0 puts it back to 1 on blur — below one would
   ask for a date after today, which is not what "days before" means. */
function DaysInput({ value, onChange }) {
  const stored = value == null ? 1 : value;
  const [draft, setDraft] = useState(String(stored));

  // Follow the config when it changes from outside (switching option resets it to 1).
  useEffect(() => {
    setDraft((current) => (Number(current) === stored ? current : String(stored)));
  }, [stored]);

  return (
    <input
      className="rsce-kvt-input"
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      placeholder="Days"
      value={draft}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 5);
        setDraft(digits);
        if (digits && Number(digits) >= 1) onChange(Number(digits));
      }}
      onBlur={() => {
        if (!draft || Number(draft) < 1) {
          setDraft("1");
          onChange(1);
        }
      }}
    />
  );
}

/* The Date Format box: still free text, plus a caret that opens every suggested
   format whatever is typed — what the antd AutoComplete did.

   The list is portalled to <body> with fixed positioning because the table
   around it (.rsce-kvt) clips overflow, which would cut the list off on the
   last row. It closes on scroll/resize rather than chasing the box. */
function FormatCombo({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const boxRef = useRef(null);

  const openList = () => {
    if (!boxRef.current) return;
    setRect(boxRef.current.getBoundingClientRect());
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const pick = (f) => {
    onChange(f);
    setOpen(false);
  };

  return (
    <div className="rsce-date-format rsce-format-combo" ref={boxRef}>
      <input
        className="rsce-kvt-input"
        type="text"
        placeholder="Date Format"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={openList}
        onClick={openList}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "ArrowDown") openList();
        }}
      />
      <button
        type="button"
        className="rsce-format-caret"
        aria-label="Show date formats"
        tabIndex={-1}
        // mousedown, not click: keeps focus in the input so its blur does not
        // close the list in the same gesture that opens it.
        onMouseDown={(e) => {
          e.preventDefault();
          if (open) setOpen(false);
          else openList();
        }}
      >
        {Icon.caret}
      </button>

      {open &&
        rect &&
        ReactDOM.createPortal(
          <ul
            className="rsce-format-menu"
            role="listbox"
            style={{ top: rect.bottom + 4, left: rect.left, width: rect.width }}
          >
            {DATE_FORMATS.map((f) => (
              <li
                key={f}
                role="option"
                aria-selected={f === value}
                className={`rsce-format-option ${f === value ? "is-selected" : ""}`}
                // mousedown so the choice lands before the input's blur closes the list
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(f);
                }}
              >
                {f}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}

export default function DateConfigRow({ config, onChange, error }) {
  const value = config || emptyDateConfig();
  const format = value.dateFormat || DEFAULT_DATE_FORMAT;

  const set = (patch) => onChange({ ...value, ...patch });

  /* Switching option starts that option clean rather than carrying the last
     one's number or date across — the antd form does the same. Today is
     resolved immediately so the stored config is never a "today" with no date
     on it. */
  const setOption = (dateOption) =>
    onChange({
      ...value,
      dateOption,
      timeUnits: 1,
      fixedDate: dateOption === "today" ? moment().format(format) : null,
    });

  /* Re-formatting has to move the stored date with it, or the value keeps the
     old format's shape while the row claims the new one. daysBefore has nothing
     to re-format — its date is worked out at run time. */
  const setFormat = (dateFormat) => {
    let fixedDate = value.fixedDate;
    if (value.dateOption === "today") {
      fixedDate = moment().format(dateFormat);
    } else if (value.dateOption === "fixedDate" && value.fixedDate) {
      const parsed = moment(value.fixedDate, format);
      fixedDate = parsed.isValid() ? parsed.format(dateFormat) : value.fixedDate;
    }
    onChange({ ...value, dateFormat, fixedDate });
  };

  /* The picker works in plain dates; the config stores whatever dateFormat says.
     An unparseable stored value leaves the picker empty rather than showing
     "Invalid date". */
  const pickerValue = (() => {
    if (!value.fixedDate) return "";
    const parsed = moment(value.fixedDate, format);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  })();

  const setFixedDate = (text) =>
    set({ fixedDate: text ? moment(text, "YYYY-MM-DD").format(format) : null });

  return (
    <div className="rsce-kvt-daterow">
      <div className="rsce-kvt-select-wrap rsce-date-option">
        <select className="rsce-kvt-select" value={value.dateOption} onChange={(e) => setOption(e.target.value)}>
          {DATE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="rsce-kvt-select-caret">{Icon.caret}</span>
      </div>

      <div className="rsce-date-mid">
        {/* PREVIOUS — a number input that clamped every change to at least 1, so
            clearing the box put the 1 straight back and you could only type a new
            count by selecting the 1 first (or use the stepper).
        {value.dateOption === "daysBefore" && (
          <input
            className="rsce-kvt-input"
            type="number"
            min={1}
            value={value.timeUnits == null ? 1 : value.timeUnits}
            onChange={(e) => set({ timeUnits: Math.max(1, Number(e.target.value) || 1) })}
          />
        )} */}
        {value.dateOption === "daysBefore" && (
          <DaysInput value={value.timeUnits} onChange={(timeUnits) => set({ timeUnits })} />
        )}

        {value.dateOption === "today" && (
          <input className="rsce-kvt-input" type="text" value={moment().format(format)} disabled readOnly />
        )}

        {value.dateOption === "fixedDate" && (
          <input
            className={`rsce-kvt-input ${error ? "has-error" : ""}`}
            type="date"
            value={pickerValue}
            onChange={(e) => setFixedDate(e.target.value)}
          />
        )}
      </div>

      {/* Free text with suggestions, not a closed list: the antd form used an
          AutoComplete, so formats outside the three have always been allowed. */}
      {/* PREVIOUS — a native <datalist>. Chrome only lists entries matching the
          text already in the box, and the box always holds a whole format, so
          its arrow showed one entry that looked like a tooltip, not a list.
      <div className="rsce-date-format">
        <input
          className="rsce-kvt-input"
          type="text"
          list="rsce-date-formats"
          placeholder="Date Format"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        />
        <datalist id="rsce-date-formats">
          {DATE_FORMATS.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
      </div> */}
      <FormatCombo value={format} onChange={setFormat} />

      {error && <div className="rsce-date-error">{error}</div>}
    </div>
  );
}
